import type {
  AutocompleteItem,
  SearchParams,
} from '@prismamedia/one-components';
import {
  NotificationTypeEnum,
  SearchBar,
  useNotification,
} from '@prismamedia/one-components';
import type { FC } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useMountedState } from 'react-use';
import {
  BrandKey,
  CategoryFormat,
} from '../../../../../../../__generated__/queries-web';
import { useApolloClient } from '@apollo/client';
import { GET_PAGE_CATEGORIES } from '../../../../../apollo/queries/categories.web.graphql';
import { CategoriesContext } from '../../../../../categories.context';
import { ALLOWED_FORMATS } from '../../../../../constants';
import { getBrandCategoriesWithPath } from '../../../../../utils';
import { SEARCHBAR_PLACEHOLDER_PREFIX } from './constants';
import { getBrandCategoriesAutoCompleteList } from './utils';

interface SearchBarPageProps {
  brandKey: BrandKey;
  format: CategoryFormat;
  onAutocompleteItemSelect: (item: AutocompleteItem) => void;
}
export const SearchBarPage: FC<SearchBarPageProps> = ({
  brandKey,
  format,
  onAutocompleteItemSelect,
}) => {
  const { activeCategory } = useContext(CategoriesContext);
  const [searchParams, setSearchParams] = useState<SearchParams>({});

  const { pushNotification } = useNotification();
  const client = useApolloClient();
  const isMounted = useMountedState();

  const searchFilteredBrandCategoriesList = useCallback(
    async (search: string): Promise<AutocompleteItem[] | undefined> => {
      setSearchParams({ search });

      try {
        if (!brandKey) {
          return;
        }
        const {
          data: { categories: dataCategories },
        } = await client.query({
          query: GET_PAGE_CATEGORIES,
          variables: {
            first: 1000,
            where: {
              brandKey,
              title_contains: search,
              format,
              level_in: [3, 4, 5],
            },
          },
        });

        if (isMounted() && dataCategories?.length) {
          const categoriesWithPathname = getBrandCategoriesWithPath(
            dataCategories,
          );

          return new Promise((resolve) => {
            // Use ALL categories instead of just the first one
            const allCategories = categoriesWithPathname || [];

            if (allCategories.length > 0) {
              const autocompleteList = getBrandCategoriesAutoCompleteList(
                activeCategory,
                allCategories, // Pass ALL categories, not just the first one
                search,
              );

              return resolve(autocompleteList);
            }

            return resolve(undefined);
          });
        }
      } catch (e) {
        if (isMounted()) {
          return new Promise((_, reject) => {
            reject(undefined);

            pushNotification({
              type: NotificationTypeEnum.error,
              message: `
                [Unable to fetch brand category events]
                ${(e as Error).message}
              `,
            });
          });
        }
      }
    },
    [activeCategory, brandKey, format, isMounted, pushNotification, client],
  );

  const searchBarPlaceholder = useMemo(() => {
    const placeholder = SEARCHBAR_PLACEHOLDER_PREFIX;

    switch (format) {
      case CategoryFormat.Category:
        return `${placeholder} ${ALLOWED_FORMATS.find(
          ({ value }) => value === CategoryFormat.Category,
        )?.label.toLowerCase()}`;
      case CategoryFormat.Event:
        return `${placeholder} ${ALLOWED_FORMATS.find(
          ({ value }) => value === CategoryFormat.Event,
        )?.label.toLowerCase()}`;
      case CategoryFormat.Channel:
        return `${placeholder} ${ALLOWED_FORMATS.find(
          ({ value }) => value === CategoryFormat.Channel,
        )?.label.toLowerCase()}`;
      case CategoryFormat.Guide:
        return `${placeholder} ${ALLOWED_FORMATS.find(
          ({ value }) => value === CategoryFormat.Guide,
        )?.label.toLowerCase()}`;
    }
  }, [format]);

  return (
    <SearchBar
      autocomplete={{
        fetchList: searchFilteredBrandCategoriesList,
        onSelect: onAutocompleteItemSelect,
      }}
      placeholder={searchBarPlaceholder}
      setSearchParams={setSearchParams}
      searchParams={searchParams}
    />
  );
};
